import type {JSONNode} from '@packages/shared/types/JSONNode';
import type {Component} from '@packages/systems/wfdl';
import {
  buildComponentInstanceJsonNodes,
  serializeWhtmlAttrs,
  serializeWhtmlText,
  type ParseRichTextBody,
  type WhtmlParserDeps,
} from './componentInstanceWhtml';

/** Capture machine for `<wf-component>` blocks, driven by the htmlparser2 callbacks in `rteHtmlToJsonNodes`. Each `handle*` returns `true` when it consumed the event; on the outer close it rebuilds the subtree via `buildComponentInstanceJsonNodes`. */
export type WhtmlCaptureController = {
  handleOpenTag: (tag: string, attributes: Record<string, string>) => boolean;
  handleText: (text: string) => boolean;
  handleComment: (comment: string) => boolean;
  handleCloseTag: (tag: string) => boolean;
};

export const createWhtmlCaptureController = (config: {
  // No-op (returns false everywhere) unless the inbound parse opted in.
  parseComponentInstanceWfdl: boolean | undefined;
  components: Component.ComponentMap | undefined;
  // Injected parser runtime (see `WhtmlParserDeps`); keeps html-to-webflow /
  // corePlugins out of this package's dependency graph.
  parserDeps: WhtmlParserDeps | undefined;
  // Passed through to `buildComponentInstanceJsonNodes`.
  parseRichTextBody?: ParseRichTextBody;
  onError?: (info: {
    instanceId: string | null;
    markup: string;
    message: string;
    reason?:
      | 'forbidden-element'
      | 'unknown-prop'
      | 'prop-type-mismatch'
      | 'nested-component-in-prop';
    // Prop name for prop-level failures, so consumers can name the prop.
    propName?: string;
    // Which forbidden element the governance check hit (`forbidden-element`
    // only), so consumers can show slot- vs collection-list-specific copy.
    forbiddenElementType?: 'DynamoWrapper' | 'Slot';
  }) => void;
  // Read lazily so we observe the live parser stack at capture-entry time.
  getCaptureParent: () => JSONNode;
  // Live refs to the parser's accumulators; `finalize` pushes onto them.
  jsonNodes: JSONNode[];
  discoveredIds: string[];
  // Validates/reserves the id for the captured instance root, applying the same
  // duplicate/transient/micro/GUID guard the normal `data-w-id` path uses, so a
  // captured component can't be persisted with an id already used by another node.
  resolveInstanceId: (candidate: string) => string;
}): WhtmlCaptureController => {
  // null when not capturing; non-null = inside a wf-component, append verbatim.
  let whtmlBuffer: string | null = null;
  let whtmlParent: JSONNode | null = null;
  // Identity preservation: stash the outer `data-w-id` and re-apply it after the parser regenerates ids.
  let whtmlInstanceId: string | null = null;
  // Open-tag depth within the capture; finalize when a close returns it to 0.
  let whtmlDepth = 0;

  const finalize = (): void => {
    const buffer = whtmlBuffer ?? '';
    const parent = whtmlParent;
    const instanceId = whtmlInstanceId;
    whtmlBuffer = null;
    whtmlParent = null;
    whtmlInstanceId = null;
    whtmlDepth = 0;

    if (parent == null) return;
    if (config.parserDeps == null) {
      config.onError?.({
        instanceId,
        markup: buffer,
        message: 'no WHTML parser deps provided to resolve the component',
      });
      return;
    }
    // Guard the stashed id against duplicates/transient/micro before preserving
    // it; an invalid or already-seen id is replaced with a freshly minted one.
    const resolvedInstanceId =
      instanceId !== null ? config.resolveInstanceId(instanceId) : null;
    // Reserve it before parsing: buildComponentInstanceJsonNodes may recurse
    // into a nested richtext prop body, and that recursive parse needs to see
    // this id as taken already, not just once this call's own nodes are
    // pushed below — otherwise a nested element reusing the same data-w-id
    // isn't caught as a duplicate.
    if (resolvedInstanceId !== null) {
      config.discoveredIds.push(resolvedInstanceId);
    }
    const {nodes, childElementIds} = buildComponentInstanceJsonNodes({
      buffer,
      instanceId: resolvedInstanceId,
      components: config.components,
      parserDeps: config.parserDeps,
      parseRichTextBody: config.parseRichTextBody,
      onError: config.onError,
    });
    for (const node of nodes) {
      config.jsonNodes.push(node);
      // The root's id is `resolvedInstanceId`, already reserved above.
      if (node._id !== resolvedInstanceId) {
        config.discoveredIds.push(node._id);
      }
    }
    for (const elementId of childElementIds) {
      if (parent.children) {
        parent.children.push(elementId);
      } else {
        parent.children = [elementId];
      }
    }
  };

  return {
    handleOpenTag: (tag, attributes) => {
      if (
        config.parseComponentInstanceWfdl &&
        whtmlBuffer === null &&
        tag === 'wf-component'
      ) {
        // Strip `data-w-id` so the parser hits INSERT mode; stash it for re-application.
        const {'data-w-id': stashedInstanceId, ...attrsForParser} = attributes;
        whtmlBuffer = `<${tag}${serializeWhtmlAttrs(attrsForParser)}>`;
        whtmlParent = config.getCaptureParent();
        whtmlInstanceId = stashedInstanceId ?? null;
        whtmlDepth = 1;
        return true;
      }
      // Inside a capture: re-serialize nested opens and bump depth.
      if (whtmlBuffer !== null) {
        whtmlBuffer += `<${tag}${serializeWhtmlAttrs(attributes)}>`;
        whtmlDepth++;
        return true;
      }
      return false;
    },
    handleText: (text) => {
      if (whtmlBuffer !== null) {
        whtmlBuffer += serializeWhtmlText(text);
        return true;
      }
      return false;
    },
    handleComment: (comment) => {
      if (whtmlBuffer !== null) {
        whtmlBuffer += `<!--${comment}-->`;
        return true;
      }
      return false;
    },
    handleCloseTag: (tag) => {
      // Re-serialize closes; finalize when the outer wf-component closes.
      if (whtmlBuffer !== null) {
        whtmlBuffer += `</${tag}>`;
        whtmlDepth--;
        if (whtmlDepth === 0) {
          finalize();
        }
        return true;
      }
      return false;
    },
  };
};
